const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

const ZERO = BigInt(0);
const ONE = BigInt(1);
const TWO = BigInt(2);
const powerOfTen = (exponent: number): bigint =>
  BigInt(`1${'0'.repeat(exponent)}`);

export const isDecimalString = (value: string): boolean =>
  DECIMAL_PATTERN.test(value);

const split = (value: string): [string, string] => {
  const [int = '0', dec = ''] = value.split('.');

  return [int || '0', dec];
};

const toScaled = (
  a: string,
  b: string,
): { aBig: bigint; bBig: bigint; fractionLength: number } => {
  const [aInt, aDec] = split(a);
  const [bInt, bDec] = split(b);
  const fractionLength = Math.max(aDec.length, bDec.length);

  return {
    aBig: BigInt(aInt + aDec.padEnd(fractionLength, '0')),
    bBig: BigInt(bInt + bDec.padEnd(fractionLength, '0')),
    fractionLength,
  };
};

const fromScaled = (value: bigint, fractionLength: number): string => {
  if (value <= ZERO) return '0';

  const digits = value.toString().padStart(fractionLength + 1, '0');
  const int = digits.slice(0, digits.length - fractionLength);
  const dec = fractionLength
    ? digits.slice(digits.length - fractionLength).replace(/0+$/, '')
    : '';

  return dec ? `${int}.${dec}` : int;
};

export const addDecimals = (a: string, b: string): string => {
  const { aBig, bBig, fractionLength } = toScaled(a, b);

  return fromScaled(aBig + bBig, fractionLength);
};

export const subtractDecimals = (a: string, b: string): string => {
  const { aBig, bBig, fractionLength } = toScaled(a, b);

  return fromScaled(aBig - bBig, fractionLength);
};

export const roundDecimalToScale = (value: string, scale: number): string => {
  const [int, dec] = split(value);

  if (scale < 0) {
    const multiple = powerOfTen(Math.abs(scale));
    const rounded = BigInt(int) + (dec.charAt(0) >= '5' ? ONE : ZERO);
    const remainder = rounded % multiple;

    return (
      remainder * TWO >= multiple
        ? rounded + (multiple - remainder)
        : rounded - remainder
    ).toString();
  }

  if (dec.length <= scale) {
    return fromScaled(BigInt(int + dec), dec.length);
  }

  const kept = BigInt(int + dec.slice(0, scale));
  const roundUp = dec.charAt(scale) >= '5';

  return fromScaled(kept + (roundUp ? ONE : ZERO), scale);
};
